import React, { useState } from 'react';
import { Box, Button, ButtonBase, Chip, Popover, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type ActiveFilter = {
  key: string;
  label: string;
  value: string;
  onDelete: () => void;
  popoverContent: React.ReactNode;
};

type FilterBarProps = {
  filterMenuButton: React.ReactNode;
  activeFilters: ActiveFilter[];
  onClearAll: () => void;
};

const FilterBar: React.FC<FilterBarProps> = ({ filterMenuButton, activeFilters, onClearAll }) => {
  const theme = useTheme();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Hover styling for each side of the chip. The keyboard-focus indicator is the
  // MUI focus ripple (via focusRipple on each ButtonBase), matching the other
  // interactive elements on the screen.
  const chipSegmentSx = {
    '&:hover': { backgroundColor: theme.palette.action.hover },
  };

  return (
    <Box sx={{
      minHeight: '40px',
      mb: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      flexWrap: 'wrap',
    }}>
      {filterMenuButton}
      {activeFilters.map(({ key, label, value, onDelete, popoverContent }) => (
        <React.Fragment key={key}>
          {/*
            The chip's label and delete control are two separate buttons so each
            is its own tab stop: the label (left) opens the filter menu, the
            delete button (right) removes the chip. They live inside a
            non-interactive Chip (no onClick/onDelete) purely for its styling, so
            the chip's root stays a plain element and we avoid a focusable control
            nested inside a chip-button (which would be invalid and inaccessible).
          */}
          <Chip
            variant='outlined'
            size='medium'
            label={
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                <ButtonBase
                  focusRipple
                  onClick={(e) => { setOpenKey(key); setAnchorEl(e.currentTarget); }}
                  aria-haspopup='dialog'
                  aria-expanded={openKey === key}
                  sx={{
                    maxWidth: '360px',
                    px: '12px',
                    py: '6px',
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    color: theme.palette.text.primary,
                    ...chipSegmentSx,
                  }}
                >
                  {/* Label never shrinks (always visible); the value takes the
                      remaining width and truncates with an ellipsis. */}
                  <Typography
                    component='span'
                    variant='bodyMedium'
                    sx={{ flexShrink: 0, whiteSpace: 'nowrap', mr: '4px' }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    component='span'
                    variant='bodyMedium'
                    sx={{
                      fontWeight: 500,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                    {value}
                  </Typography>
                </ButtonBase>
                <ButtonBase
                  focusRipple
                  onClick={onDelete}
                  aria-label={`Remove ${label} filter`}
                  sx={{
                    alignSelf: 'stretch',
                    borderLeft: '1px solid',
                    borderLeftColor: theme.palette.grey[300],
                    px: '6px',
                    ...chipSegmentSx,
                  }}
                >
                  <CloseIcon sx={{ fontSize: '24px', color: theme.palette.text.primary }} />
                </ButtonBase>
              </Box>
            }
            sx={{
              borderRadius: '4px',
              borderColor: theme.palette.grey[300],
              height: 'auto',
              overflow: 'hidden',
              '& .MuiChip-label': {
                p: 0,
                display: 'flex',
                overflow: 'visible',
              },
            }}
          />
          <Popover
            open={openKey === key}
            anchorEl={openKey === key ? anchorEl : undefined}
            onClose={() => setOpenKey(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {popoverContent}
          </Popover>
        </React.Fragment>
      ))}
      {activeFilters.length > 0 && (
        <Button
          size='small'
          onClick={onClearAll}
          sx={{ textTransform: 'none', fontSize: '14px' }}
        >
          Clear All
        </Button>
      )}
    </Box>
  );
};

export default FilterBar;
